import { EXPERIMENTS, feedKey, PREDICT_FEED, RU_FEED, TRANSFERS } from '../lib/feedContext';
/**
 * Состояние экрана: лента, призраки, метаданные, выделение.
 *
 * Отдельный стор, а не расширение `store/feed.ts`, и это решение о границах, а
 * не о вкусе. Тот стор построен вокруг фильтров, паузы и скрытых руками строк —
 * механики старого экрана. Здесь регулятор один, порядок замораживается под
 * курсором, а ушедшая вилка не исчезает молча, а остаётся призраком с названной
 * причиной. Слить это в один объект значило бы, что оба экрана правятся вместе
 * и ломаются вместе.
 */

import { create } from 'zustand';

import { tellBookFromFork } from '../book/capture';
import { type Outcome, report, reportFor } from '../lib/attempt';
import { dropHiddenBooks } from '../lib/hideBooks';
import type { Fork } from '../lib/protocol';
import { noteFate } from './fates';
import { EMPTY_META, keepFunnel, readFunnel, withVenueCashAll, type Meta } from './meta';
import { buryPaper } from './paper';

/** Сколько призрак держится на своём месте, мс. +200 к четырём секундам (14.10). */
export const GHOST_MS = 4200;
/** Сколько горит подсветка новой строки, мс. */
export const BORN_MS = 1400;

export type Mode = 'showcase' | 'feed';

export interface Ghost {
  fork: Fork;
  /** Код причины из каталога `deaths`. Подпись берётся из `/v1/meta`. */
  why: string;
  at: number;
}

/**
 * Вилка, убранная крестиком раскрытой карточки. Не режим и не фильтр профиля:
 * человек сам убрал эту строку с глаз. F5 обнуляет — иначе завтра лента молчит.
 */
export interface HiddenFork {
  fork: Fork;
  /** `null` — спрятали живую. Иначе код смерти на момент ухода. */
  why: string | null;
  at: number;
}

/** Рабочая лента или то, что убрали крестиком. Не пишется в localStorage. */
export type Shelf = 'work' | 'hidden';

/**
 * `paused` — сервер сам сказал, что уходит на выкат и вернётся. Отличается от
 * `reconnecting` не оттенком, а смыслом: там связь пропала неизвестно почему и
 * неизвестно насколько, здесь названы и причина, и срок.
 */
export type Status = 'connecting' | 'live' | 'reconnecting' | 'paused' | 'revoked';

export interface ScreenState {
  status: Status;
  /**
   * Пришёл ли хотя бы один кадр ленты. Сокет открывается раньше, чем доезжает
   * первый КАДР, и в эту щель лента пуста при живом статусе — экран говорил
   * «вилок нет, рынок спокоен», хотя вилки просто ещё в дороге (07.08,
   * владелец). Пустоту можно объявлять только по настоящему пустому кадру.
   */
  framed: boolean;
  /**
   * Когда пришёл последний кадр ленты. `null` — ни одного ещё не было.
   *
   * Не то же, что возраст вилки: возраст говорит, сколько она живёт на рынке, а
   * это — сколько лет НАШИМ цифрам. Молчащий сокет при живом статусе выглядит
   * как спокойный рынок, и отличить одно от другого можно только этим числом.
   */
  frameAt: number | null;
  forks: Map<string, Fork>;
  ghosts: Map<string, Ghost>;
  /**
   * Что убрали крестиком. Живая остаётся в `forks` и не возвращается сама:
   * человек её спрятал. Пункт «скрытые» в сите ленты читает отсюда.
   */
  hiddenForks: Map<string, HiddenFork>;
  /** Какой список на экране. Пустой раздел открывается: иначе его не найти. */
  shelf: Shelf;
  /** Когда строка появилась на этом экране. Нужно для вспышки новой. */
  bornAt: Map<string, number>;
  meta: Meta;
  /**
   * Счётчики скрытого на момент открытия вкладки. Сервер отдаёт монотонные
   * серии с запуска процесса, и «за смену убрано 42» получается вычитанием.
   * Своей сессии у сервера нет намеренно: она стоила бы состояния на каждого
   * зрителя ради одной цифры в подвале.
   */
  baseline: Record<string, number> | null;
  /**
   * Сколько вилок не влезло в кадр. Число приходит с каждым снапшотом: кадр
   * ограничен байтами, и на многолюдной ленте сервер отдаёт лучшие по ROI, а
   * остаток называет числом. Молчать об этом нельзя — подвал обещает объяснить
   * каждую строку, которой на экране нет.
   */
  cutByFrame: number;
  mode: Mode;
  /** Выделенная строка (клавиатура) и раскрытая (enter). */
  sel: string | null;
  open: string | null;
  /** Порядок заморожен: курсор над лентой или строка раскрыта. */
  frozen: boolean;
  /** Порядок, зафиксированный на момент заморозки. */
  order: string[];
  /** Секундный тик: возраст строк идёт локально, сервер его не гоняет. */
  now: number;
  /**
   * Отметки исполнения по строкам: когда человек ПОДТВЕРДИЛ, что ставка стоит.
   * Разница между первой и второй и есть его темп — единственная величина в
   * этом экране, которую мы знаем точно, а не предполагаем.
   *
   * Подтверждение отделено от открытия конторы намеренно (08.08, владелец).
   * Прежде отметка ставилась на нажатие «открыть и скопировать», то есть на
   * НАМЕРЕНИЕ: человек, дважды ткнувший кнопки из любопытства и не поставивший
   * ничего, получал измеренный темп 56 с — и по этой выдумке лента раздавала
   * пометки «может не дожить». Открытая контора — это ещё не деньги на кону;
   * считается только сказанное вслух «поставил».
   *
   * `third` — для трёхногих вилок (1-Х-2); темп по-прежнему меряется парой
   * первая→вторая, третья отметка в замер не входит.
   * `vow` — прибыль, которую строка обещала В МОМЕНТ первой ноги. Сравнивать
   * «ушла ли цена» с текущей прибылью нельзя: она пересчитана из тех же
   * подешевевших цен и совпадает с планом закрытия всегда.
   * `opened` — когда контора этой ноги была открыта. Держит вилку «в работе» и
   * решает, спрашивать ли «поставили?»; в замер темпа не входит.
   * `failed` — человек сказал «не вышло»: цена ушла, лимит, отказ формы.
   */
  steps: Record<
    string,
    {
      first: number | null;
      second: number | null;
      third: number | null;
      vow: number | null;
      opened: [number | null, number | null, number | null];
      failed: [boolean, boolean, boolean];
    }
  >;
  /**
   * Что человек отодвинул на витрине кнопкой «дальше». Живёт до перезагрузки
   * вкладки и не уезжает на сервер: это решение про эту минуту, а не вкус.
   * Держится списком, а не флагом на строке, потому что строки приходят заново
   * каждым снапшотом.
   */
  skipped: string[];
  /**
   * Вилка, выбранная из очереди руками. Отдельно от `skipped`, потому что
   * «покажи вон ту» и «эти мне не нужны» — разные просьбы: выбор из очереди не
   * должен выбрасывать всё, что стояло выше.
   */
  pinned: string | null;
  /**
   * Вилки в отдельном окне из живой ленты. Держим в кадре, как раскрытую
   * строку, но саму строку не раскрываем: значок «окном» — один жест.
   */
  popped: Set<string>;
  /**
   * Набитое плечо калькулятора. Живёт, пока вилку держат карточка, окно
   * или полка: закрыл карточку — замок не снимается, второе плечо едет
   * с кэфом. «Закрыл» на полке или другая вилка — снова раскладка от банка.
   */
  stakeLock: { forkId: string; source: string; form: string; as?: 'usd' | 'share' } | null;
  /**
   * Свои кэфы / центы. Тот же срок, что у замка суммы. Пока жив, лента
   * число не затирает. Галочка «с ленты» снимает.
   */
  oddsLock: { forkId: string; by: Record<string, string> } | null;
  /** Комиссия ноги, процентами. Тот же срок, что у кэфа. */
  feeLock: { forkId: string; by: Record<string, string> } | null;
  /** Валюта кассы ноги: ₽ или $, поверх каталога площадки. */
  curLock: { forkId: string; by: Record<string, 'RUB' | 'USD'> } | null;
  /** Фрибет SNR на одной ноге раскрытой вилки. */
  freeLock: { forkId: string; source: string } | null;
  /** Третья нога руками. Только если в кадре две. */
  handLock: { forkId: string; odds: string } | null;
  /**
   * Порядок плиток БК, как в момент клика по карточке или окну.
   * Пока карточка или отдельное окно живы — места не пересчитываются.
   * Закрыл оба — замок снят, следующее открытие берёт тот кадр.
   */
  seatLock: Record<string, string[]>;

  setStatus: (status: Status) => void;
  apply: (forks: Map<string, Fork>, cutByFrame?: number) => void;
  noteGone: (id: string, why: string) => void;
  setMeta: (meta: Meta) => void;
  /** Воронка с кадра сокета. `/v1/meta` её не несёт и не имеет права затирать. */
  noteFunnel: (raw: unknown) => void;
  /** tickBroken с дельты: Empty не ждёт 30 с опроса /v1/meta. */
  noteTick: (raw: Record<string, unknown>) => void;
  /** Proto с кадра сокета: HTTP `/v1/meta` не имеет права держать ленту. */
  noteProto: (proto: number) => void;
  setMode: (mode: Mode) => void;
  setSel: (id: string | null) => void;
  setOpen: (id: string | null) => void;
  setStakeLock: (lock: ScreenState['stakeLock']) => void;
  /** Снять замки, если вилку больше не держат ни карточка, ни окно, ни полка. */
  releaseLocks: (id: string) => void;
  /** Полка переехала на живой id — замки едут следом, иначе сумма сбрасывается. */
  moveLocks: (from: string, to: string) => void;
  setOddsLock: (lock: ScreenState['oddsLock']) => void;
  setFeeLock: (lock: ScreenState['feeLock']) => void;
  setCurLock: (lock: ScreenState['curLock']) => void;
  /** Вбить или снять свой кэф одной ноги. Пустой `form` снимает. */
  setOddsQuote: (forkId: string, source: string, form: string | null) => void;
  /** Вбить или снять комиссию ноги. Пустой `form` снимает. */
  setFeeQuote: (forkId: string, source: string, form: string | null) => void;
  /** Переключить валюту ноги. `null` — снова каталог площадки. */
  setCurQuote: (forkId: string, source: string, cur: 'RUB' | 'USD' | null) => void;
  /** Фрибет на ноге. `null` — снова обычная ставка. */
  setFreeLeg: (forkId: string, source: string | null) => void;
  /** Третья нога. `null` — убрать. */
  setHandOdds: (forkId: string, odds: string | null) => void;
  /** Запомнить расклад плиток. Повторный вызов тот же id не переписывает. */
  holdSeats: (id: string, seats: readonly string[]) => void;
  /** Окно вилки открыто: держать строку, карточку в ленте не раскрывать. */
  pop: (id: string) => void;
  unpop: (id: string) => void;
  /** Заморозить порядок. Повторный вызов порядок не переписывает. */
  freeze: (ids: readonly string[]) => void;
  thaw: () => void;
  /** Курсор ушёл с ленты: снять выделение наведения и оттаять порядок. */
  leave: () => void;
  tick: (now: number) => void;
  /**
   * Отметить прошедшую ногу. Возвращает секунды между ногами, когда обе прошли.
   * `promised` — прибыль строки на момент отметки; запоминается на первой ноге.
   */
  confirmLeg: (id: string, leg: 1 | 2 | 3, promised?: number) => number | null;
  /** Контора ноги открыта: человек пошёл ставить, но ещё не сказал, что поставил. */
  openLeg: (id: string, leg: 1 | 2 | 3) => void;
  /**
   * «Не вышло»: цена ушла, лимит, отказ формы. Ставки нет, темп не измеряется.
   * Код площадки называют вызывающие: по нему считается доля отказов конторы,
   * которой проверяется порядок ног.
   */
  failLeg: (id: string, leg: 1 | 2 | 3, venue?: string) => void;
  /** Отодвинуть вилку на витрине: показать следующую. */
  skip: (id: string) => void;
  /** Поднять вилку из очереди на витрину. */
  pick: (id: string) => void;
  /** Убрать вилку из рабочей ленты в раздел «скрытые». Крестик карточки. */
  hide: (id: string) => void;
  /** Вернуть спрятанную, если она ещё в кадре. Мёртвая с сервера не врёт «в ленту». */
  unhide: (id: string) => void;
  setShelf: (shelf: Shelf) => void;
  /** То же, что `hide`: старое имя держали тесты и баннер смерти. */
  release: (id: string) => void;
  /** fork_id уже в кассе: повтор не плодит строку, в ленте тихая пометка. */
  booked: Record<string, true>;
}

/**
 * Вилка «в работе»: человек нажал хотя бы одну кнопку ноги или держит карточку
 * раскрытой. Такая строка не имеет права исчезнуть из-под рук (07.08,
 * владелец): человек, не успевший открыть вторую контору, возвращался к ленте,
 * где его вилки уже нет — без объяснения и без цифр, по которым он ставил.
 */
/** Карточка или окно этой вилки ещё на экране — замки калькулятора живы. */
/**
 * Дополнительные id, которые нельзя отдавать призраку на растерзание.
 * Полка калькулятора регистрируется отсюда, а не импортом: иначе store ↔ clip
 * замкнулись бы по загрузке модуля.
 */
const extraHeld = new Map<string, () => Iterable<string>>();

export function holdMore(name: string, read: () => Iterable<string>): void {
  extraHeld.set(name, read);
}

function stillCalc(
  state: Pick<ScreenState, 'open' | 'popped'>,
  forkId: string,
): boolean {
  if (state.open === forkId || state.popped.has(forkId)) return true;
  for (const read of extraHeld.values()) {
    for (const id of read()) if (id === forkId) return true;
  }
  return false;
}

/** Снять замки вилки, если её больше не считают ни карточка, ни окно. */
function dropLocks(state: ScreenState, forkId: string): Partial<ScreenState> {
  if (stillCalc(state, forkId)) return {};
  return {
    ...(state.stakeLock?.forkId === forkId ? { stakeLock: null } : {}),
    ...(state.oddsLock?.forkId === forkId ? { oddsLock: null } : {}),
    ...(state.feeLock?.forkId === forkId ? { feeLock: null } : {}),
    ...(state.curLock?.forkId === forkId ? { curLock: null } : {}),
    ...(state.freeLock?.forkId === forkId ? { freeLock: null } : {}),
    ...(state.handLock?.forkId === forkId ? { handLock: null } : {}),
  };
}

/** Расклад плиток живёт, пока открыта карточка или отдельное окно. Полка не держит. */
function dropSeat(
  state: Pick<ScreenState, 'open' | 'popped' | 'seatLock'>,
  forkId: string,
): Partial<ScreenState> {
  if (state.open === forkId || state.popped.has(forkId)) return {};
  if (state.seatLock[forkId] === undefined) return {};
  const seatLock = { ...state.seatLock };
  delete seatLock[forkId];
  return { seatLock };
}

export function heldIds(
  state: Pick<ScreenState, 'steps' | 'open' | 'popped'> & {
    hiddenForks?: Map<string, HiddenFork>;
  },
): Set<string> {
  const buried = state.hiddenForks;
  const ids = new Set<string>();
  for (const [id, step] of Object.entries(state.steps)) {
    // Открытая контора держит строку наравне с поставленной ногой: человек
    // ушёл на чужую вкладку и вернётся сюда за суммой второй ноги.
    if (buried?.has(id)) continue;
    if (step.first !== null || step.opened.some((at) => at !== null)) ids.add(id);
  }
  if (state.open !== null && !buried?.has(state.open)) ids.add(state.open);
  for (const id of state.popped) if (!buried?.has(id)) ids.add(id);
  for (const read of extraHeld.values()) {
    for (const id of read()) {
      if (!buried?.has(id)) ids.add(id);
    }
  }
  return ids;
}

/** Держать в кадре работу и спрятанных: иначе живая скрытая пропадёт из снимка. */
export function watchIds(state: Pick<ScreenState, 'steps' | 'open' | 'popped' | 'hiddenForks'>): Set<string> {
  const ids = heldIds(state);
  for (const id of state.hiddenForks.keys()) ids.add(id);
  return ids;
}

/**
 * Отметить попытку на сервере.
 *
 * Живёт в хранилище, а не в кнопках, намеренно: ног касаются и витрина, и
 * карточка, и горячие клавиши, а замер обязан быть один. Отсчёт идёт от
 * ОТКРЫТИЯ первой конторы — с этого момента человек в дороге; отсчёт от первой
 * поставленной ноги спрятал бы ровно ту часть пути, ради которой всё
 * затевалось: загрузку сайта и поиск матча.
 */
function tellServer(
  fork: Fork | undefined,
  step: ScreenState['steps'][string],
  outcome: Outcome,
  failedVenue?: string,
): void {
  if (fork === undefined) return;
  const opened = step.opened.filter((at): at is number => at !== null);
  if (opened.length === 0) return;
  report(
    reportFor(fork, outcome, {
      startedAt: Math.min(...opened),
      firstAt: step.first,
      secondAt: step.second,
      failedVenue,
    }),
  );
}

/** Пустые отметки строки. Форма одна на все места, где шаг заводится впервые. */
export const blankStep = (): ScreenState['steps'][string] => ({
  first: null,
  second: null,
  third: null,
  vow: null,
  opened: [null, null, null],
  failed: [false, false, false],
});

const MODE_KEY = feedKey('forq.next.mode');

function savedMode(): Mode {
  if (TRANSFERS || RU_FEED || PREDICT_FEED || EXPERIMENTS) return 'feed';
  try {
    return localStorage.getItem(MODE_KEY) === 'feed' ? 'feed' : 'showcase';
  } catch {
    return 'showcase';
  }
}

export const useScreen = create<ScreenState>((set, get) => ({
  status: 'connecting',
  framed: false,
  frameAt: null,
  forks: new Map(),
  ghosts: new Map(),
  hiddenForks: new Map(),
  shelf: 'work',
  bornAt: new Map(),
  meta: EMPTY_META,
  baseline: null,
  cutByFrame: 0,
  mode: savedMode(),
  sel: null,
  open: null,
  frozen: false,
  order: [],
  now: Date.now(),
  steps: {},
  booked: {},
  skipped: [],
  pinned: null,
  popped: new Set(),
  stakeLock: null,
  oddsLock: null,
  feeLock: null,
  curLock: null,
  freeLock: null,
  handLock: null,
  seatLock: {},

  setStatus: (status) => set({ status }),

  apply: (frame, cutByFrame) => {
    const now = Date.now();
    const state = get();
    const forks = dropHiddenBooks(withVenueCashAll(frame, state.meta));
    const previous = state.bornAt;

    // Призрак живёт на своём месте несколько секунд, потом уходит сам. Вернулась
    // та же вилка — призрак снимается сразу: две одинаковые строки, одна из
    // которых «умерла», это способ поставить дважды.
    // Вилку «в работе» это правило не касается: она держится, пока человек её
    // не отпустит, — деньги, возможно, уже стоят на первой ноге.
    const held = heldIds(state);
    const ghosts = new Map(state.ghosts);
    for (const [id, ghost] of ghosts) {
      if (state.hiddenForks.has(id) || forks.has(id) || (now - ghost.at > GHOST_MS && !held.has(id))) {
        ghosts.delete(id);
      }
    }
    // Снапшот без события «ушла»: серая строка вместо вспышки рождения, когда
    // та же вилка скачет на пороге. Пустой кадр (обрыв) не серит всю ленту —
    // только работу, как раньше.
    if (forks.size > 0) {
      for (const [id, prev] of state.forks) {
        if (forks.has(id) || ghosts.has(id) || state.hiddenForks.has(id)) continue;
        ghosts.set(id, { fork: prev, why: 'other', at: now });
      }
    } else {
      for (const id of held) {
        if (forks.has(id) || ghosts.has(id) || state.hiddenForks.has(id)) continue;
        const gone = state.forks.get(id);
        if (gone !== undefined) ghosts.set(id, { fork: gone, why: 'other', at: now });
      }
    }
    // Первую встречу помним и у призрака: иначе скачущая вилка каждый раз
    // вспыхивает как новая (14.10, Сергей).
    const bornAt = new Map<string, number>();
    for (const [id, at] of previous) {
      if (forks.has(id) || ghosts.has(id) || held.has(id)) bornAt.set(id, at);
    }
    for (const id of forks.keys()) {
      if (!bornAt.has(id)) bornAt.set(id, now);
    }
    // Спрятанная живая обновляет снимок. Сама в ленту не вылезает.
    const hiddenForks = new Map(state.hiddenForks);
    for (const [id, hid] of hiddenForks) {
      const live = forks.get(id);
      if (live !== undefined) hiddenForks.set(id, { ...hid, fork: live, why: null });
    }
    set({
      forks,
      bornAt,
      ghosts,
      hiddenForks,
      now,
      framed: true,
      frameAt: now,
      ...(cutByFrame === undefined ? {} : { cutByFrame }),
    });
  },

  noteGone: (id, why) => {
    const hid = get().hiddenForks.get(id);
    if (hid !== undefined) {
      const hiddenForks = new Map(get().hiddenForks);
      hiddenForks.set(id, { ...hid, why, at: Date.now() });
      set({ hiddenForks });
      noteFate(id, why, get().bornAt.get(id), Number(hid.fork.roi ?? 0));
      buryPaper(id);
      return;
    }
    const fork = get().forks.get(id);
    // Причина без вилки — нечего показывать. Так бывает после переподключения:
    // снапшот пришёл уже без неё.
    if (fork === undefined) return;
    const ghosts = new Map(get().ghosts);
    ghosts.set(id, { fork, why, at: Date.now() });
    set({ ghosts });
    // Смерть проходит здесь ровно один раз — это и делает место единственно
    // пригодным для журнала судеб. Считать по исчезновению строки из ленты
    // значило бы записывать одну вилку столько раз, сколько было перерисовок.
    noteFate(id, why, get().bornAt.get(id), Number(fork.roi ?? 0));
    // Бумажная ставка закрывается здесь же: её судьба и есть «дожила ли вилка
    // до того, как человек дошёл бы до второй ноги».
    buryPaper(id);
  },

  setMeta: (meta) => {
    const first = get().baseline === null && Object.keys(meta.hidden).length > 0;
    const funnel = meta.funnel ?? get().meta.funnel;
    const proto = meta.proto > 0 ? meta.proto : get().meta.proto;
    const next = { ...meta, funnel, proto };
    set(first ? { meta: next, baseline: { ...meta.hidden } } : { meta: next });
  },

  noteProto: (proto) => {
    if (!Number.isFinite(proto) || proto <= 0) return;
    const meta = get().meta;
    if (meta.proto === proto) return;
    set({ meta: { ...meta, proto } });
  },

  noteFunnel: (raw) => {
    const incoming = readFunnel(raw);
    if (incoming === null) return;
    const meta = get().meta;
    const funnel = keepFunnel(meta.funnel, incoming);
    if (meta.funnel === funnel) return;
    set({ meta: { ...meta, funnel } });
  },

  noteTick: (raw) => {
    if (!('tickBroken' in raw) && !('tickFailures' in raw)) return;
    const meta = get().meta;
    const tickBroken = raw.tickBroken === true;
    const tickFailures =
      typeof raw.tickFailures === 'number' && Number.isFinite(raw.tickFailures)
        ? raw.tickFailures
        : meta.tickFailures;
    const lastFailureAgeSeconds =
      typeof raw.lastFailureAgeSeconds === 'number' && Number.isFinite(raw.lastFailureAgeSeconds)
        ? raw.lastFailureAgeSeconds
        : raw.lastFailureAgeSeconds === null
          ? null
          : meta.lastFailureAgeSeconds;
    if (
      meta.tickBroken === tickBroken &&
      meta.tickFailures === tickFailures &&
      meta.lastFailureAgeSeconds === lastFailureAgeSeconds
    ) {
      return;
    }
    set({ meta: { ...meta, tickBroken, tickFailures, lastFailureAgeSeconds } });
  },

  setMode: (mode) => {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      // Приватный режим — не повод не переключить экран.
    }
    set({ mode });
  },

  setSel: (sel) => set({ sel }),
  setOpen: (open) => {
    const prev = get();
    const leaving = prev.open !== null && prev.open !== open ? prev.open : null;
    const next = { ...prev, open };
    set({
      open,
      ...(leaving !== null ? { ...dropLocks(next, leaving), ...dropSeat(next, leaving) } : {}),
    });
  },
  setStakeLock: (stakeLock) => set({ stakeLock }),
  releaseLocks: (id) => {
    const next = dropLocks(get(), id);
    if (Object.keys(next).length > 0) set(next);
  },
  moveLocks: (from, to) => {
    if (from === '' || to === '' || from === to) return;
    const s = get();
    const next: Partial<ScreenState> = {
      ...(s.stakeLock?.forkId === from ? { stakeLock: { ...s.stakeLock, forkId: to } } : {}),
      ...(s.oddsLock?.forkId === from ? { oddsLock: { ...s.oddsLock, forkId: to } } : {}),
      ...(s.feeLock?.forkId === from ? { feeLock: { ...s.feeLock, forkId: to } } : {}),
      ...(s.curLock?.forkId === from ? { curLock: { ...s.curLock, forkId: to } } : {}),
      ...(s.freeLock?.forkId === from ? { freeLock: { ...s.freeLock, forkId: to } } : {}),
      ...(s.handLock?.forkId === from ? { handLock: { ...s.handLock, forkId: to } } : {}),
    };
    if (s.seatLock[from] !== undefined) {
      const seatLock = { ...s.seatLock };
      seatLock[to] = seatLock[from]!;
      delete seatLock[from];
      next.seatLock = seatLock;
    }
    if (Object.keys(next).length > 0) set(next);
  },
  setOddsLock: (oddsLock) => set({ oddsLock }),
  setFeeLock: (feeLock) => set({ feeLock }),
  setCurLock: (curLock) => set({ curLock }),
  setOddsQuote: (forkId, source, form) => {
    const cur = get().oddsLock;
    const by = cur?.forkId === forkId ? { ...cur.by } : {};
    if (form === null) delete by[source];
    else by[source] = form;
    set({ oddsLock: Object.keys(by).length === 0 ? null : { forkId, by } });
  },
  setFeeQuote: (forkId, source, form) => {
    const cur = get().feeLock;
    const by = cur?.forkId === forkId ? { ...cur.by } : {};
    if (form === null) delete by[source];
    else by[source] = form;
    set({ feeLock: Object.keys(by).length === 0 ? null : { forkId, by } });
  },
  setCurQuote: (forkId, source, cur) => {
    const now = get().curLock;
    const by = now?.forkId === forkId ? { ...now.by } : {};
    if (cur === null) delete by[source];
    else by[source] = cur;
    set({ curLock: Object.keys(by).length === 0 ? null : { forkId, by } });
  },
  setFreeLeg: (forkId, source) => {
    set({ freeLock: source === null ? null : { forkId, source } });
  },
  setHandOdds: (forkId, odds) => {
    set({ handLock: odds === null ? null : { forkId, odds } });
  },
  holdSeats: (id, seats) => {
    if (id === '' || seats.length === 0) return;
    if (get().seatLock[id] !== undefined) return;
    set({ seatLock: { ...get().seatLock, [id]: [...seats] } });
  },
  pop: (id) => {
    if (get().popped.has(id)) return;
    const popped = new Set(get().popped);
    popped.add(id);
    set({ popped });
  },
  unpop: (id) => {
    if (!get().popped.has(id)) return;
    const popped = new Set(get().popped);
    popped.delete(id);
    const next = { ...get(), popped };
    set({ popped, ...dropLocks(next, id), ...dropSeat(next, id) });
  },

  // Порядок запоминается в момент входа в заморозку и потом не трогается.
  // Иначе каждое движение мыши по ленте пересобирало бы список заново — то
  // есть заморозка не морозила бы ничего.
  freeze: (ids) => {
    if (get().frozen) return;
    set({ frozen: true, order: [...ids] });
  },
  thaw: () => {
    // Пока строка выделена или раскрыта, порядок держится: человек работает
    // именно с ней, и уехавший из-под рук список — это ставка не по той цене.
    const state = get();
    if (state.sel !== null || state.open !== null) return;
    set({ frozen: false, order: [] });
  },

  // Курсор ушёл с ленты. Наведение оставляло за собой выделение, и `thaw` из-за
  // него не оттаивал НИКОГДА: после первого касания мышью порядок замерзал
  // навсегда, свежие строки дописывались вниз как попало, и мёртвые вилки
  // стояли наверху вопреки сортировке (08.08, владелец — «почему всё красное
  // в топе»). Раскрытая карточка порядок по-прежнему держит.
  leave: () => {
    const state = get();
    if (state.open !== null) {
      set({ sel: null });
      return;
    }
    set({ sel: null, frozen: false, order: [] });
  },
  tick: (now) => set({ now }),

  confirmLeg: (id, leg, promised) => {
    const at = Date.now();
    const current = get().steps[id] ?? blankStep();
    const key = (['first', 'second', 'third'] as const)[leg - 1]!;
    const failed = [...current.failed] as [boolean, boolean, boolean];
    // Поставил после «не вышло» — со второй попытки получилось; отказ снимается.
    failed[leg - 1] = false;
    const next = { ...current, [key]: at, failed };
    if (leg === 1 && promised !== undefined) next.vow = promised;
    set({ steps: { ...get().steps, [id]: next }, booked: { ...get().booked, [id]: true } });
    // Касса: та же отметка, без формы. Слово в книге — «отмечена вами».
    tellBookFromFork(get().forks.get(id), {
      naked: next.failed.some(Boolean),
      error: next.failed.some(Boolean),
    });
    // Темп — это пара первая→вторая и только она: третья нога трёхногой вилки
    // подтверждается после того, как гонка со временем уже состоялась.
    if (leg !== 2 || next.first === null || next.second === null) return null;
    tellServer(get().forks.get(id), next, 'both');
    return Math.max(1, Math.round((next.second - next.first) / 1000));
  },

  openLeg: (id, leg) => {
    const current = get().steps[id] ?? blankStep();
    // Открыть контору можно дважды (вкладку закрыли, вернулись) — время держим
    // от ПЕРВОГО открытия: с него человек в дороге, и с него вилка обязана жить.
    if (current.opened[leg - 1] !== null) return;
    const opened = [...current.opened] as [number | null, number | null, number | null];
    opened[leg - 1] = Date.now();
    set({ steps: { ...get().steps, [id]: { ...current, opened } } });
  },

  failLeg: (id, leg, venue) => {
    const current = get().steps[id] ?? blankStep();
    const failed = [...current.failed] as [boolean, boolean, boolean];
    failed[leg - 1] = true;
    const next = { ...current, failed };
    set({ steps: { ...get().steps, [id]: next } });
    // Отказ на первой ноге стоит ноль, на второй — голой позиции. Это разные
    // события, и в журнале они обязаны различаться. Площадка называется здесь
    // же: по ней потом проверяется порядок ног — сегодня он расставлен по
    // рассуждению о механизме отказа, а не по замеру (`next/order.ts`).
    tellServer(get().forks.get(id), next, current.first === null ? 'none' : 'one', venue);
    if (current.first !== null || current.second !== null) {
      tellBookFromFork(get().forks.get(id), { naked: true, error: true });
    }
  },

  skip: (id) => {
    // Отодвинули ту, что была поднята руками, — снимаем и подъём: иначе она
    // вернулась бы на витрину следующим же кадром.
    const pinned = get().pinned === id ? null : get().pinned;
    if (get().skipped.includes(id)) {
      set({ pinned });
      return;
    }
    const stakeLock = get().stakeLock?.forkId === id ? null : get().stakeLock;
    const oddsLock = get().oddsLock?.forkId === id ? null : get().oddsLock;
    const feeLock = get().feeLock?.forkId === id ? null : get().feeLock;
    const curLock = get().curLock?.forkId === id ? null : get().curLock;
    const freeLock = get().freeLock?.forkId === id ? null : get().freeLock;
    const handLock = get().handLock?.forkId === id ? null : get().handLock;
    const now = get();
    const seatGone = dropSeat(now, id);
    set({
      skipped: [...get().skipped, id],
      pinned,
      stakeLock,
      oddsLock,
      feeLock,
      curLock,
      freeLock,
      handLock,
      ...seatGone,
    });
  },

  pick: (id) => set({ pinned: id }),

  hide: (id) => {
    const state = get();
    const fork =
      state.forks.get(id) ?? state.ghosts.get(id)?.fork ?? state.hiddenForks.get(id)?.fork;
    const open = state.open === id ? null : state.open;
    if (fork === undefined) {
      if (open !== state.open) {
        const gone = { ...state, open };
        set({ open, ...dropLocks(gone, id), ...dropSeat(gone, id) });
      }
      return;
    }
    const ghost = state.ghosts.get(id);
    const prev = state.hiddenForks.get(id);
    const hiddenForks = new Map(state.hiddenForks);
    hiddenForks.set(id, {
      fork,
      why: ghost?.why ?? prev?.why ?? null,
      at: ghost?.at ?? prev?.at ?? Date.now(),
    });
    const ghosts = new Map(state.ghosts);
    ghosts.delete(id);
    const next = { ...state, open, hiddenForks, ghosts };
    set({
      hiddenForks,
      ghosts,
      open,
      ...dropLocks(next, id),
      ...dropSeat(next, id),
    });
  },

  unhide: (id) => {
    const state = get();
    if (!state.hiddenForks.has(id) || !state.forks.has(id)) return;
    const hiddenForks = new Map(state.hiddenForks);
    hiddenForks.delete(id);
    set({
      hiddenForks,
      ...(hiddenForks.size === 0 ? { shelf: 'work' as const } : {}),
    });
  },

  setShelf: (shelf) => {
    set({ shelf });
  },

  release: (id) => get().hide(id),
}));

/**
 * Сколько строк скрыто с моего прихода, по категориям.
 *
 * Считается вычитанием из монотонной серии, поэтому переживает и рестарт
 * сервера: счётчик, поехавший вниз, даёт ноль, а не отрицательное число —
 * «убрано минус три» не значит ничего.
 */
export function hiddenSince(
  hidden: Record<string, number>,
  baseline: Record<string, number> | null,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(hidden)) {
    const was = baseline?.[key] ?? value;
    out[key] = Math.max(0, value - was);
  }
  return out;
}
